/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 * Auth ServiceAccess sagas
 *
 * @author Yakov Gushchin
 * @author Ilya Baranchikov
 * @version 1.27.0
 * @since 1.11.0
 */

import { SagaIterator } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects';

import { setNotificationsSettings } from '@/main/modules/userNotifications/slice';
import { requestSaga } from '@/main/sagas/requestSaga';
import { API_HOSTNAME, API_SERVICE_ACCESS_CHANGE_USER, LOGIN_USER_SERVICE_ACCESS } from '@modules/app/Api';
import { appHistoryPush } from '@modules/app/appHistory';
import { makeSelectTranslation } from '@modules/internationalization/intSelectors';
import { ITranslation } from '@modules/internationalization/types';
import { hideLoader, showLoader } from '@modules/loader/loaderActions';
import { closeNotificationAction } from '@modules/notification/notificationActions';
import { removeStorageDataStart } from '@modules/storage/storageActions';
import { RoutePath } from '@routes/constants/routesConstants';

import { logoutStartFailure, logoutStartSuccessful } from '../actions/authActions';
import { loginProcessServiceAccessSuccessfulAction } from '../actions/authServiceAccessActions';
import { TLoginUserServiceAccessAction } from '../types/authServiceAccessTypes';
import { IAuthData } from '../types/authTypes';
import {
    loginProcessFail,
    loginProcessNoUserFailSaga,
    loginProcessSendBrowserTimezoneSaga,
    loginProcessSetCookieSaga,
    loginProcessStartUserCacheSaga,
    loginProcessSuccessSaga,
    objectToFormData,
} from './loginProcessSaga';

const options = {
    headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        'X-NODE': 'sticky',
    },
    throwIfHasError: true,
};

export function* loginUserServiceAccessSaga(action: TLoginUserServiceAccessAction): SagaIterator {
    const translation: ITranslation = yield select(makeSelectTranslation());
    try {
        yield put(showLoader());
        const response: IAuthData = yield call(
            requestSaga,
            API_HOSTNAME + LOGIN_USER_SERVICE_ACCESS,
            'POST',
            objectToFormData(action.payload),
            options,
        );

        if (response.notifications) {
            yield put(
                setNotificationsSettings({
                    showNotifications: response.notifications.showNotifications,
                    showNews: response.notifications.showNews,
                }),
            );
        }

        if (response.user) {
            // step 1 - If cached is false we request a cache
            yield call(loginProcessStartUserCacheSaga, response, translation);
            // step 2 - Set default accountId to cookie
            yield call(loginProcessSetCookieSaga, response);
            // step 3 - Send browser timezone
            yield call(loginProcessSendBrowserTimezoneSaga, translation);
            // step 4 - Login success
            yield call(loginProcessSuccessSaga, response);
        } else {
            yield call(loginProcessNoUserFailSaga, response, translation);
        }
    } catch (error: any) {
        yield call(loginProcessFail, error.error || error, translation);
    } finally {
        yield put(hideLoader());
    }
}

export function* changeUserServiceAccessSaga(): SagaIterator {
    try {
        yield put(removeStorageDataStart());
        yield call(requestSaga, API_SERVICE_ACCESS_CHANGE_USER, 'GET');
        yield put(logoutStartSuccessful());
        yield put(closeNotificationAction());
    } catch (e: any) {
        yield put(closeNotificationAction());
        yield put(logoutStartFailure(e));
    } finally {
        yield put(loginProcessServiceAccessSuccessfulAction());
        yield call(appHistoryPush, RoutePath.serviceAccessLogin);
    }
}
